/**
 * What an organization decides for every machine in it, beyond a setting a
 * member could otherwise hold (§5.25, §7.1) — its **policy**.
 *
 * A setting, even an enforced one, is a value: one answer laid over the
 * member's own. The network lists are not values a machine has one of. The
 * allowlist is already a union of three (Airlock's built-ins, the
 * application's, each workspace's), and a blocklist is only worth anything if
 * no other layer can take a name off it. So they are not rows of `defaults`;
 * they are this, and they reach every workspace on a member's machine — the
 * organization's copies and the member's own, since it is the person who is a
 * member, not the folder.
 *
 *   egress.allowedDomains  added to every workspace's allowlist
 *   egress.blockedDomains  refused everywhere: taken out of every allowlist a
 *                          person or a workspace wrote, never asked about, and
 *                          refused when somebody tries to allow it on a machine
 *
 * A blocked name covers its subdomains too (`example.com` blocks
 * `api.example.com`): a blocklist a subdomain walks round is not one. What it
 * does not reach is Airlock's own built-in list (the agents' hosts, the
 * package registries), which every session needs to run at all — that list is
 * the desktop's, not a document's, and the account page says so.
 *
 *   models                 per agent, the only models its members' runs may
 *                          name — a task, a step, a side agent or an agent's
 *                          own; an agent with no list here runs any of its own
 *
 *   pullRequests           the rules for the pull requests Airlock opens and the
 *                          branches it lands on by itself, in the organization's
 *                          own workspaces (`pull-request-rules.ts`)
 *   extensions             the library of MCP servers, skills and plugins its
 *                          members switch on per workspace, and whether it is
 *                          the only thing a session may be given
 *                          (`extension-library.ts`)
 *   rules                  whether a merge in its workspaces waits for a Rules
 *                          audit (`rules-policy.ts`)
 *   targets                what a member's machine may not do with a target
 *                          (`targets-policy.ts`)
 *   sharedTargets          the targets it shares with its own workspaces,
 *                          their secrets named, never valued (`shared-targets.ts`)
 *
 * Which **agents** may run is not here: it is the onboarding `agents` setting,
 * and an organization that locks it has said which agents its members use. The
 * two together are what a run is held to (`runRefusal`).
 *
 * Kept apart from `settings.ts` so the desktop's hot path (`settingsFor`) and
 * the account page can read it without the validator's catalogue behind it.
 */
import { AGENT_IDS, agentLabel, isAgentId, type AgentId } from '../settings/agents';
import { MODELS, agentOfModel } from '../settings/models';
import { EGRESS_DOMAIN, MAX_EGRESS_DOMAINS, blockedBy } from '../settings/resolve';
import type { EgressMode } from '../settings/types';
import { isPlainObject } from '../workspace/shape';
import { EMPTY_ORG_EXTENSIONS, orgExtensionsOf, parseOrgExtensions, type OrgExtensions } from './extension-library';
import { EMPTY_PULL_REQUEST_RULES, parsePullRequestRules, pullRequestRulesOf, type OrgPullRequests } from './pull-request-rules';
import { EMPTY_ORG_RULES, orgRulesOf, parseOrgRules, type OrgRules } from './rules-policy';
import { EMPTY_ORG_TARGETS, orgTargetsOf, parseOrgTargets, type OrgTargets } from './targets-policy';
import { parseSharedTargets, sharedTargetsOf, type OrgSharedTarget } from './shared-targets';

export interface OrgEgress {
  allowedDomains: string[];
  blockedDomains: string[];
}

/** Per agent, the models a run may name; an agent absent from it is not narrowed. */
export type OrgModels = Partial<Record<AgentId, string[]>>;

export interface OrgPolicy {
  egress: OrgEgress;
  models: OrgModels;
  /** Optional in a document — one written before the rules existed has none; `policyOf` always fills it. */
  pullRequests?: OrgPullRequests;
  /** Optional in a document for the same reason. */
  extensions?: OrgExtensions;
  /** Optional in a document for the same reason. */
  rules?: OrgRules;
  /** Optional in a document for the same reason (`targets-policy.ts`). */
  targets?: OrgTargets;
  /** Optional in a document for the same reason (`shared-targets.ts`). */
  sharedTargets?: OrgSharedTarget[];
}

/** The policy as `policyOf` reads it: every section present. */
export type ReadPolicy = Required<OrgPolicy>;

export const EMPTY_ORG_POLICY: ReadPolicy = {
  egress: { allowedDomains: [], blockedDomains: [] },
  models: {},
  pullRequests: EMPTY_PULL_REQUEST_RULES,
  extensions: EMPTY_ORG_EXTENSIONS,
  rules: EMPTY_ORG_RULES,
  targets: EMPTY_ORG_TARGETS,
  sharedTargets: []
};

/**
 * The policy as a document carries it, or the empty one. A record written
 * before policies existed has none, and an install reading an older Worker's
 * answer gets none — both mean "nothing decided", never a failure.
 */
export function policyOf(settings: { policy?: unknown } | null | undefined): ReadPolicy {
  const raw = settings?.policy;
  if (!isPlainObject(raw)) return EMPTY_ORG_POLICY;
  const list = (value: unknown): string[] =>
    Array.isArray(value) ? value.filter((one): one is string => typeof one === 'string' && EGRESS_DOMAIN.test(one)) : [];
  const egress = isPlainObject(raw.egress) ? raw.egress : {};
  const models: OrgModels = {};
  if (isPlainObject(raw.models)) {
    for (const agent of AGENT_IDS) {
      const named = raw.models[agent];
      if (!Array.isArray(named)) continue;
      const kept = named.filter((one): one is string => typeof one === 'string' && agentOfModel(one) === agent);
      if (kept.length) models[agent] = kept;
    }
  }
  return {
    egress: { allowedDomains: list(egress.allowedDomains), blockedDomains: list(egress.blockedDomains) },
    models,
    pullRequests: pullRequestRulesOf(raw.pullRequests),
    extensions: orgExtensionsOf(raw.extensions),
    rules: orgRulesOf(raw.rules),
    targets: orgTargetsOf(raw.targets),
    sharedTargets: sharedTargetsOf(raw.sharedTargets)
  };
}

/**
 * The policy as an administrator wrote it — refused with a sentence per entry
 * rather than filtered, unlike a machine's own lists: a document that silently
 * lost a blocked host would be a policy that says one thing and does another.
 */
export function parseOrgPolicy(input: unknown, problems: string[]): OrgPolicy {
  if (input === undefined || input === null) return EMPTY_ORG_POLICY;
  if (!isPlainObject(input)) {
    problems.push('policy: Expected an object.');
    return EMPTY_ORG_POLICY;
  }
  for (const key of Object.keys(input)) {
    if (!['egress', 'models', 'pullRequests', 'extensions', 'rules', 'targets', 'sharedTargets'].includes(key)) problems.push(`policy.${key}: Not a field this version of Airlock knows.`);
  }
  const models = parseModels(input.models, problems);
  const pullRequests = parsePullRequestRules(input.pullRequests, problems);
  const extensions = parseOrgExtensions(input.extensions, problems);
  const rules = parseOrgRules(input.rules, problems);
  const targets = parseOrgTargets(input.targets, problems);
  const sharedTargets = parseSharedTargets(input.sharedTargets, problems);
  const egress = input.egress ?? {};
  if (!isPlainObject(egress)) {
    problems.push('policy.egress: Expected an object.');
    return { ...EMPTY_ORG_POLICY, models, pullRequests, extensions, rules, targets, sharedTargets };
  }
  for (const key of Object.keys(egress)) {
    if (key !== 'allowedDomains' && key !== 'blockedDomains') problems.push(`policy.egress.${key}: Not a field this version of Airlock knows.`);
  }
  const allowedDomains = domainList(egress.allowedDomains, 'policy.egress.allowedDomains', problems);
  const blockedDomains = domainList(egress.blockedDomains, 'policy.egress.blockedDomains', problems);
  for (const host of allowedDomains) {
    if (blockedBy(blockedDomains, host)) problems.push(`policy.egress.allowedDomains: ${host} is also blocked — a host is one or the other.`);
  }
  return { egress: { allowedDomains, blockedDomains }, models, pullRequests, extensions, rules, targets, sharedTargets };
}

/** Each agent's list: its own models, at least one, once each. */
function parseModels(value: unknown, problems: string[]): OrgModels {
  if (value === undefined || value === null) return {};
  if (!isPlainObject(value)) {
    problems.push('policy.models: Expected an object.');
    return {};
  }
  const models: OrgModels = {};
  for (const [agent, named] of Object.entries(value)) {
    if (!isAgentId(agent)) {
      problems.push(`policy.models.${agent}: Not an agent Airlock runs.`);
      continue;
    }
    if (!Array.isArray(named) || !named.length) {
      problems.push(`policy.models.${agent}: Expected a list of at least one model — leave the agent out to allow all of its own.`);
      continue;
    }
    const kept: string[] = [];
    for (const model of named) {
      if (typeof model !== 'string' || agentOfModel(model) !== agent) {
        problems.push(`policy.models.${agent}: ${String(model)} is not one of ${agentLabel(agent)}’s models.`);
      } else if (kept.includes(model)) problems.push(`policy.models.${agent}: ${model} is listed twice.`);
      else kept.push(model);
    }
    if (kept.length) models[agent] = kept;
  }
  return models;
}

function domainList(value: unknown, at: string, problems: string[]): string[] {
  if (value === undefined || value === null) return [];
  if (!Array.isArray(value)) {
    problems.push(`${at}: Expected a list.`);
    return [];
  }
  const out: string[] = [];
  for (const entry of value) {
    const host = typeof entry === 'string' ? entry.trim().toLowerCase() : '';
    if (!EGRESS_DOMAIN.test(host)) problems.push(`${at}: ${String(entry)} is not a plain host name — no scheme, path, port or wildcard.`);
    else if (out.includes(host)) problems.push(`${at}: ${host} is listed twice.`);
    else out.push(host);
  }
  if (out.length > MAX_EGRESS_DOMAINS) problems.push(`${at}: ${MAX_EGRESS_DOMAINS} hosts at most.`);
  return out.slice(0, MAX_EGRESS_DOMAINS);
}

// Whether a blocklist covers a host — its rule lives beside the domain rule.
export { blockedBy } from '../settings/resolve';

/**
 * Every organization this machine is a member of, collapsed into what a
 * workspace on it resolves against (§7.1).
 *
 * The lists **combine**: a host any of them blocks is blocked (each one's
 * refusal is a promise to its own administrators, and no other organization
 * can lift it), and a host any of them allows is allowed unless one blocks it.
 * Each blocked name keeps the organization that blocks it, so a refusal can
 * name it.
 *
 * The mode is the one a single answer has to come from: the first
 * organization, in the order given, that **holds** `egressMode` decides it —
 * the same "first one that says anything wins" every held setting follows.
 */
export interface MemberEgress {
  allowed: string[];
  blocked: { domain: string; org: string }[];
  mode: { value: EgressMode; org: string } | null;
}

export function memberEgress(
  orgs: readonly { name: string; policy: OrgPolicy; mode?: EgressMode | null }[]
): MemberEgress {
  const blocked: MemberEgress['blocked'] = [];
  for (const org of orgs) {
    for (const domain of org.policy.egress.blockedDomains) {
      if (!blocked.some((one) => one.domain === domain)) blocked.push({ domain, org: org.name });
    }
  }
  const names = blocked.map((one) => one.domain);
  const allowed = [...new Set(orgs.flatMap((org) => org.policy.egress.allowedDomains))].filter((host) => !blockedBy(names, host));
  const holder = orgs.find((org) => org.mode);
  return { allowed, blocked, mode: holder?.mode ? { value: holder.mode, org: holder.name } : null };
}

/** Whether a member's machine has anything to apply at all. */
export function egressDecides(egress: MemberEgress | null | undefined): boolean {
  return !!egress && (egress.allowed.length > 0 || egress.blocked.length > 0 || egress.mode !== null);
}

/** The organization that blocks a host, or null. */
export function blockerOf(egress: MemberEgress | null | undefined, host: string): string | null {
  return egress?.blocked.find((one) => blockedBy([one.domain], host))?.org ?? null;
}

/**
 * What a run is held to on a member's machine (§5.10): the agents an
 * organization locks (its held `agents`), and each agent's models (`models`),
 * each named after the organization that decided it. Several organizations: the
 * first to answer wins — the same order every held setting is read in.
 */
export interface MemberAgents {
  agents: { allowed: AgentId[]; org: string } | null;
  models: Partial<Record<AgentId, { allowed: string[]; org: string }>>;
}

export function memberAgents(
  orgs: readonly { name: string; policy: OrgPolicy }[],
  heldAgents: { allowed: readonly string[]; org: string } | null
): MemberAgents {
  const models: MemberAgents['models'] = {};
  for (const org of orgs) {
    for (const agent of AGENT_IDS) {
      const allowed = org.policy.models[agent];
      if (allowed?.length && !models[agent]) models[agent] = { allowed, org: org.name };
    }
  }
  const agents = heldAgents ? { allowed: AGENT_IDS.filter((id) => heldAgents.allowed.includes(id)), org: heldAgents.org } : null;
  return { agents: agents && agents.allowed.length ? agents : null, models };
}

/** Whether nothing narrows a run at all. */
export function agentsDecide(policy: MemberAgents | null | undefined): boolean {
  return !!policy && (policy.agents !== null || Object.keys(policy.models).length > 0);
}

/** Whether a model may be run under the policy — what a picker filters its options by. */
export function modelAllowed(policy: MemberAgents | null | undefined, model: string): boolean {
  return runRefusal(policy, model, '') === null;
}

/**
 * Why a run naming `model` may not start, in a sentence beginning with `what`
 * ("This task", "This step"), or null when it may.
 */
export function runRefusal(policy: MemberAgents | null | undefined, model: string, what: string): string | null {
  if (!policy || !model) return null;
  const agent = agentOfModel(model);
  if (!agent) return null;
  const label = MODELS.find((one) => one.value === model)?.label ?? model;
  if (policy.agents && !policy.agents.allowed.includes(agent)) {
    const allowed = policy.agents.allowed.map(agentLabel).join(' and ');
    return `${what} runs ${agentLabel(agent)} (${label}), and ${policy.agents.org} allows only ${allowed} on its members’ computers.`;
  }
  const models = policy.models[agent];
  if (models && !models.allowed.includes(model)) {
    const allowed = models.allowed.map((one) => MODELS.find((spec) => spec.value === one)?.label ?? one).join(', ');
    return `${what} runs ${label}, and ${models.org} allows only ${allowed} for ${agentLabel(agent)} on its members’ computers.`;
  }
  return null;
}
